import {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import {
    buildColumnKey,
    fetchInvoiceTransferHistory,
    revertGlInvoiceTransfer,
    type GlColumnDef,
    type GlImportAmountDetailSnap,
    type GlInvoiceTransferHistoryVO,
} from '@/services/accountService/glInvoiceService';
import {isRateGlCode} from '@/components/account/gl-invoice/mode/tabs/bankColumnUtils';

export type GlTransferHistoryDialogProps = {
    open: boolean;
    companyId: number;
    annualId: number;
    companyName?: string;
    columnDefs?: GlColumnDef[];
    onClose: () => void;
    /** 还原成功后刷新 Tiny / Sheet */
    onReverted?: () => void | Promise<void>;
};

const FS_FALLBACK_LABEL: Record<string, string> = {
    'BS-CA-PND': 'Paid no docs',
    'BS-CA-PPM': 'Prepayment',
    'BS-CA-CASH': 'Bank Balance and Cash',
    'BS-CL-AP': 'Account payable',
    'BS-EQ-IC': 'Share capital',
    'PL-COS-PUR': 'Purchase',
    'BS-NCA-PPE': 'Property, plant and equipment',
};

function formatTime(value?: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
}

function formatMoney(value?: number | null): string {
    if (value == null || Number.isNaN(Number(value))) return '';
    const n = Number(value);
    if (n < 0) {
        return `(${Math.abs(n).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})})`;
    }
    return n.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

function isHkdCurrency(currency?: string | null): boolean {
    const cur = (currency ?? 'HKD').trim().toUpperCase();
    return !cur || cur === 'HKD';
}

function isBankFs(fsCode?: string | null): boolean {
    return (fsCode || '') === 'BS-CA-CASH';
}

function sumHkdAmount(details?: GlImportAmountDetailSnap[], preferNonBank = true): number | null {
    if (!details || details.length === 0) return null;
    const hkdLines = details.filter((d) =>
        !isRateGlCode(d.glCode) && isHkdCurrency(d.currency),
    );
    if (hkdLines.length === 0) return null;
    const preferred = preferNonBank
        ? hkdLines.filter((d) => d.fsCode && !isBankFs(d.fsCode))
        : hkdLines;
    const use = preferred.length > 0 ? preferred : hkdLines.filter((d) => isBankFs(d.fsCode));
    if (use.length === 0) return null;
    return use.reduce((s, d) => s + (Number(d.amount) || 0), 0);
}

function resolveTransferAmountHkd(row: GlInvoiceTransferHistoryVO): number | null {
    return sumHkdAmount(row.afterDetails) ?? sumHkdAmount(row.beforeDetails);
}

type LabelMaps = {
    byKey: Map<string, string>;
    byFs: Map<string, string>;
};

function buildLabelMaps(columnDefs: GlColumnDef[]): LabelMaps {
    const byKey = new Map<string, string>();
    const byFs = new Map<string, string>();
    for (const d of columnDefs) {
        const key = buildColumnKey(d);
        const label = d.nameEn || key;
        byKey.set(key, label);
        if (d.fsCode && !byFs.has(d.fsCode)) {
            byFs.set(d.fsCode, label);
        }
    }
    return {byKey, byFs};
}

function resolveCategoryLabel(fs: string, gl: string | null | undefined, maps: LabelMaps): string {
    if (!fs) return '—';
    const glCode = gl || '';
    const exact = maps.byKey.get(`${fs}|${glCode}`);
    if (exact) return exact;
    const byFs = maps.byFs.get(fs);
    if (byFs) return byFs;
    return FS_FALLBACK_LABEL[fs] || fs;
}

function resolveFromLabel(
    details: GlImportAmountDetailSnap[] | undefined,
    maps: LabelMaps,
): string {
    if (!details || details.length === 0) return '—';
    const meaningful = details.filter((d) =>
        d.fsCode && !isBankFs(d.fsCode) && !isRateGlCode(d.glCode),
    );
    const pool = meaningful.length > 0 ? meaningful : details.filter((d) => d.fsCode && !isRateGlCode(d.glCode));
    if (pool.length === 0) return '—';
    const d = pool[0];
    return resolveCategoryLabel(d.fsCode || '', d.glCode, maps);
}

function resolveToLabel(row: GlInvoiceTransferHistoryVO, maps: LabelMaps): string {
    if (row.afterDetails && row.afterDetails.length > 0) {
        const meaningful = row.afterDetails.filter((d) =>
            d.fsCode && !isBankFs(d.fsCode) && !isRateGlCode(d.glCode),
        );
        const d = meaningful[0] || row.afterDetails.find((x) => x.fsCode && !isRateGlCode(x.glCode)) || row.afterDetails[0];
        return resolveCategoryLabel(d.fsCode || '', d.glCode, maps);
    }
    if (row.targetFsCode) {
        return resolveCategoryLabel(row.targetFsCode, row.targetGlCode, maps);
    }
    return '—';
}

function isForwardTransfer(row: GlInvoiceTransferHistoryVO): boolean {
    return !row.operationSource || row.operationSource === 'TINY_TRANSFER';
}

/** 该票在列表中是否仍有更新的 transfer/revert（按列表时间序） */
function hasNewerInList(rows: GlInvoiceTransferHistoryVO[], row: GlInvoiceTransferHistoryVO): boolean {
    const idx = rows.findIndex((r) => r.batchId === row.batchId && r.invoiceId === row.invoiceId);
    if (idx < 0) return true;
    // rows 已按时间倒序；同 invoice 若出现在更前位置则为更新
    for (let i = 0; i < idx; i++) {
        if (rows[i].invoiceId === row.invoiceId) return true;
    }
    return false;
}

/** Tiny Mode 发票转移历史。 */
export default function GlTransferHistoryDialog(props: GlTransferHistoryDialogProps) {
    const {open, companyId, annualId, companyName, columnDefs = [], onClose, onReverted} = props;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<GlInvoiceTransferHistoryVO[]>([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [revertingKey, setRevertingKey] = useState<string | null>(null);

    const labelMaps = useMemo(() => buildLabelMaps(columnDefs), [columnDefs]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await fetchInvoiceTransferHistory(companyId, annualId);
            setRows(Array.isArray(list) ? list : []);
            setPage(0);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to load transfer history');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId]);

    useEffect(() => {
        if (open) void load();
    }, [open, load]);

    const batchInvoiceCount = useMemo(() => {
        const map = new Map<number, number>();
        for (const r of rows) {
            if (!isForwardTransfer(r)) continue;
            map.set(r.batchId, (map.get(r.batchId) || 0) + 1);
        }
        return map;
    }, [rows]);

    /** 每个 batch 只在第一行显示 Revert Batch */
    const firstForwardRowOfBatch = useMemo(() => {
        const seen = new Set<number>();
        const keys = new Set<string>();
        for (const r of rows) {
            if (!isForwardTransfer(r)) continue;
            if (seen.has(r.batchId)) continue;
            seen.add(r.batchId);
            keys.add(`${r.batchId}-${r.invoiceId}`);
        }
        return keys;
    }, [rows]);

    const paged = useMemo(() => {
        const start = page * pageSize;
        return rows.slice(start, start + pageSize);
    }, [rows, page, pageSize]);

    const handleRevert = useCallback(async (row: GlInvoiceTransferHistoryVO, wholeBatch: boolean) => {
        const key = wholeBatch ? `batch-${row.batchId}` : `${row.batchId}-${row.invoiceId}`;
        setRevertingKey(key);
        setError(null);
        try {
            await revertGlInvoiceTransfer({
                companyId,
                annualId,
                batchId: row.batchId,
                invoiceId: wholeBatch ? null : row.invoiceId,
            });
            await load();
            await onReverted?.();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Revert failed');
        } finally {
            setRevertingKey(null);
        }
    }, [annualId, companyId, load, onReverted]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Transfer History
                {companyName ? (
                    <Typography variant="body2" color="text.secondary" component="span" sx={{ml: 1}}>
                        — {companyName}
                    </Typography>
                ) : null}
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{py: 4, textAlign: 'center'}}>
                        <CircularProgress size={28} />
                    </Box>
                ) : error ? (
                    <Typography color="error" sx={{mb: 1}}>{error}</Typography>
                ) : null}
                {!loading && rows.length === 0 && !error ? (
                    <Typography color="text.secondary" sx={{py: 2}}>
                        No transfer records yet.
                    </Typography>
                ) : null}
                {!loading && rows.length > 0 && (
                    <>
                        {error && (
                            <Typography color="error" sx={{mb: 1}}>{error}</Typography>
                        )}
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Time</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell sx={{minWidth: 240}}>Transfer</TableCell>
                                        <TableCell align="right">Amount (HKD)</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paged.map((r) => {
                                        const fromLabel = resolveFromLabel(r.beforeDetails, labelMaps);
                                        const toLabel = resolveToLabel(r, labelMaps);
                                        const transferText = `${fromLabel} → ${toLabel}`;
                                        const forward = isForwardTransfer(r);
                                        const canRevert = forward && !hasNewerInList(rows, r);
                                        const batchSize = batchInvoiceCount.get(r.batchId) || 0;
                                        const showBatchBtn = canRevert
                                            && batchSize > 1
                                            && firstForwardRowOfBatch.has(`${r.batchId}-${r.invoiceId}`);
                                        const rowKey = `${r.batchId}-${r.invoiceId}`;
                                        const busy = revertingKey != null;
                                        return (
                                            <TableRow key={rowKey} hover>
                                                <TableCell sx={{whiteSpace: 'nowrap'}}>
                                                    {formatTime(r.changeTime)}
                                                </TableCell>
                                                <TableCell sx={{whiteSpace: 'nowrap'}}>
                                                    {r.date || '—'}
                                                </TableCell>
                                                <TableCell sx={{maxWidth: 200}}>
                                                    <Typography variant="body2" noWrap title={r.description || ''}>
                                                        {r.description || '—'}
                                                        {!forward && (
                                                            <Typography component="span" variant="caption" color="text.secondary" sx={{ml: 0.5}}>
                                                                (reverted)
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600} title={transferText}>
                                                        {fromLabel}
                                                        <Typography component="span" color="text.secondary" sx={{mx: 0.75}}>
                                                            →
                                                        </Typography>
                                                        {toLabel}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                                    {formatMoney(resolveTransferAmountHkd(r))}
                                                </TableCell>
                                                <TableCell>
                                                    {r.changeUserId != null ? r.changeUserId : '—'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {canRevert ? (
                                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                            <Button
                                                                size="small"
                                                                startIcon={
                                                                    revertingKey === rowKey
                                                                        ? <CircularProgress size={12} color="inherit" />
                                                                        : <UndoIcon fontSize="small" />
                                                                }
                                                                disabled={busy}
                                                                onClick={() => void handleRevert(r, false)}
                                                                sx={{textTransform: 'none', minWidth: 0}}
                                                            >
                                                                Revert
                                                            </Button>
                                                            {showBatchBtn && (
                                                                <Button
                                                                    size="small"
                                                                    color="secondary"
                                                                    disabled={busy}
                                                                    onClick={() => void handleRevert(r, true)}
                                                                    sx={{textTransform: 'none', minWidth: 0}}
                                                                >
                                                                    {revertingKey === `batch-${r.batchId}`
                                                                        ? '…'
                                                                        : `Batch (${batchSize})`}
                                                                </Button>
                                                            )}
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={rows.length}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={(e) => {
                                setPageSize(parseInt(e.target.value, 10) || 25);
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50]}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={1} sx={{px: 1}}>
                    <Button onClick={() => void load()} disabled={loading} sx={{textTransform: 'none'}}>
                        Refresh
                    </Button>
                    <Button onClick={onClose} sx={{textTransform: 'none'}}>Close</Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
